import { useMemo } from 'react'
import { Vector3 } from 'three'
import { useAppStore } from '@stores'
import AreaLoadMeshElement from '../AreaLoadMeshElement'
import LineLoadMeshElement from '../LineLoadMeshElement'
import PointLoadMeshElement from '../PointLoadMeshElement'
import { createLineLoadDisplayData, createPointLoadDisplayData } from './utils'

interface Props {
  loads: Array<DomainLoad | AreaLoad>
  onClick?: (load: ElementLoad) => void
  highlightedIds?: string[]
  hiddenElements?: Set<string>
  opacity?: number
}

const MAX_FACTOR = 1.5
const GEOMETRY_THICKNESS = (0.015 * 0.5) / MAX_FACTOR
const SMALL_MINIMUM_AVOID_DIV_BY_ZERO = 0.0001

const SimpleLoadMesh = ({
  loads,
  onClick = () => null,
  highlightedIds,
  hiddenElements = new Set(),
  opacity = 1,
}: Props) => {
  const loadDisplayScale = useAppStore(state => state.loadDisplayScale)
  const lineLoadRenderConfig = useAppStore(state => state.loadRenderConfig.lineLoads)
  const pointLoadRenderConfig = useAppStore(state => state.loadRenderConfig.pointLoads)

  const lineLoadLabelFormat = useMemo(
    () => ({
      showUnitsInScene: lineLoadRenderConfig.showUnitsInScene,
      showUnitInNewLine: lineLoadRenderConfig.showUnitInNewLine,
    }),
    [lineLoadRenderConfig.showUnitsInScene, lineLoadRenderConfig.showUnitInNewLine],
  )

  const pointLoadLabelFormat = useMemo(
    () => ({
      showUnitsInScene: pointLoadRenderConfig.showUnitsInScene,
      showUnitInNewLine: pointLoadRenderConfig.showUnitInNewLine,
    }),
    [pointLoadRenderConfig.showUnitsInScene, pointLoadRenderConfig.showUnitInNewLine],
  )

  // compute load attribute that is to be diplayed from loads
  const loadsDisplayData = useMemo(() => {
    const visibleLoads = loads.filter(({ element_guid }) => !hiddenElements.has(element_guid))
    const visibleLineLoads = visibleLoads.filter(
      load => load.load_type === 'line-load',
    ) as LineLoad[]

    // Calculate the maximum line load magnitude across all visible line loads
    const visibleLineLoadsMaxMagnitude = Math.max(
      ...visibleLineLoads.flatMap(load => [
        new Vector3(load.start.x, load.start.y, load.start.z).length(),
        new Vector3(load.end.x, load.end.y, load.end.z).length(),
      ]),
      SMALL_MINIMUM_AVOID_DIV_BY_ZERO, // Small minimum to avoid division by zero
    )

    return visibleLoads.map(load => {
      const { load_type } = load

      if (load_type === 'line-load') {
        const { domain } = load
        // Calculate thickness based on load magnitude relative to max magnitude
        const loadMaxMagnitude = Math.max(
          new Vector3(load.start.x, load.start.y, load.start.z).length(),
          new Vector3(load.end.x, load.end.y, load.end.z).length(),
        )
        const thickness =
          (MAX_FACTOR - loadMaxMagnitude / visibleLineLoadsMaxMagnitude) * GEOMETRY_THICKNESS
        return {
          load,
          data: createLineLoadDisplayData({
            load,
            domainStart: domain.start,
            domainEnd: domain.end,
            scale: loadDisplayScale,
            domain,
            thickness,
            labelFormat: lineLoadLabelFormat,
          }),
        }
      } else if (load_type === 'point-load') {
        const { domain } = load

        return {
          load,
          data: createPointLoadDisplayData({
            load,
            domainStart: domain.start,
            domainEnd: domain.end,
            scale: loadDisplayScale,
            domain,
            labelFormat: pointLoadLabelFormat,
          }),
        }
      } else {
        return {
          load,
          data: {},
        }
      }
    })
  }, [loads, hiddenElements, loadDisplayScale, lineLoadLabelFormat, pointLoadLabelFormat])

  return (
    <>
      {loadsDisplayData.map(({ load, data }) => {
        if (load.load_type === 'line-load') {
          return (
            <LineLoadMeshElement
              key={load.guid}
              load={load}
              onClick={() => onClick(load)}
              loadDisplayData={data as LineLoadDisplayData}
              isActive={highlightedIds && highlightedIds.includes(load.guid)}
              opacity={opacity}
            />
          )
        }

        if (load.load_type === 'point-load') {
          return (
            <PointLoadMeshElement
              load={load}
              key={load.guid}
              onClick={() => onClick(load)}
              loadDisplayData={data as PointLoadDisplayData}
              isActive={highlightedIds && highlightedIds.includes(load.guid)}
              opacity={opacity}
            />
          )
        }

        if (load.load_type === 'area-load') {
          return (
            <AreaLoadMeshElement
              key={load.guid}
              onClick={() => onClick(load)}
              load={load}
              isActive={highlightedIds && highlightedIds.includes(load.guid)}
              opacity={opacity}
            />
          )
        }

        return null
      })}
    </>
  )
}

export default SimpleLoadMesh
